import { AppShell } from './AppShell';
import type { NavGroup, NavTabs } from './nav-types';

/**
 * The six tools that used to hide behind the "More" hub are first-class rows
 * here — a sidebar has the room the top bar never did, and a destination one
 * click away is not a destination worth a landing page. /student/more stays
 * routable for anyone who bookmarked it.
 */
const GROUPS: readonly NavGroup[] = [
  {
    label: 'Overview',
    items: [
      { to: '/student/home', label: 'Home', icon: 'home' },
      { to: '/student/progress', label: 'Progress', icon: 'progress' },
      // Same slot as the institute sidebar: one AppShell, one mental model.
      // The row carries its own unread count — there is no second control
      // anywhere in the shell claiming the same number.
      {
        to: '/student/notifications',
        label: 'Notifications',
        icon: 'notifications',
        badge: 'notifications',
        matchSubRoutes: true,
      },
    ],
  },
  {
    label: 'Practise',
    items: [
      { to: '/student/sessions', label: 'Sessions', icon: 'sessions' },
      { to: '/student/assignments', label: 'Assignments', icon: 'assignment' },
      { to: '/student/practice', label: 'Mock tests', icon: 'practice' },
      {
        to: '/student/skill-practice',
        label: 'Question types',
        icon: 'questionTypes',
        matchSubRoutes: true,
      },
      { to: '/student/shadowing', label: 'Shadowing', icon: 'shadowing' },
      { to: '/student/vocab', label: 'Vocabulary', icon: 'vocab', matchSubRoutes: true },
    ],
  },
  {
    label: 'My record',
    items: [
      // Phase 179a. One of four entry points, because a message nobody finds
      // is a message nobody answers: this row, the Notifications row above,
      // the home card, and the notification's own deep link.
      { to: '/student/messages', label: 'Messages', icon: 'messages', matchSubRoutes: true },
      { to: '/student/mistakes', label: 'My mistakes', icon: 'mistakes' },
      { to: '/student/results', label: 'Results', icon: 'results', matchSubRoutes: true },
      { to: '/student/activity', label: 'Enrolment', icon: 'activity' },
      { to: '/student/documents', label: 'Documents', icon: 'documents' },
      { to: '/student/billing', label: 'Billing', icon: 'billing', matchSubRoutes: true },
    ],
  },
  {
    label: 'Resources',
    items: [
      { to: '/student/materials', label: 'Study materials', icon: 'materials' },
      { to: '/student/study-plan', label: 'AI study plan', icon: 'studyPlan' },
      { to: '/student/tutor', label: 'Tutor', icon: 'tutor', disabled: true },
    ],
  },
  // One row per workspace, in the same slot, because the account block in the
  // sidebar foot is easy to miss on a phone where the drawer scrolls.
  {
    label: 'Account',
    items: [{ to: '/student/profile', label: 'Profile', icon: 'account' }],
  },
];

/**
 * The phone's four. "Practise" is the question-type drills, not the mock
 * library: drills are the phone-sized daily loop (Phases 161–167) and the one
 * flow with sub-routes a lit tab should follow. A 2h45 mock is one tap away
 * in More.
 */
const TABS: NavTabs = [
  { to: '/student/home', label: 'Home', icon: 'home' },
  { to: '/student/skill-practice', label: 'Practise', icon: 'questionTypes', matchSubRoutes: true },
  { to: '/student/results', label: 'Results', icon: 'results', matchSubRoutes: true },
  { to: '/student/mistakes', label: 'Mistakes', icon: 'mistakes' },
];

export function StudentLayout(): JSX.Element {
  return (
    <AppShell
      brand="IELTS Mock"
      workspace="Student"
      profileHref="/student/profile"
      homeHref="/student/home"
      groups={GROUPS}
      tabs={TABS}
    />
  );
}
